import React, { useContext, useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { UserContext } from "../App";
import logo from "../assets/logo.png";
import "./Header.css";
import LogoutButton from "./LogoutButton";
import { useTrainingSection } from "../hooks/useTrainingElement";
import { useUnsavedChanges } from "../contexts/UnsavedChangesContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBookOpen,
  faFileAlt,
  faUser,
  faGraduationCap,
  faHome,
  faRobot,
  faShieldAlt,
} from "@fortawesome/free-solid-svg-icons";
// Import all avatar images directly
import Avatar0 from "../assets/avatars/Avatar0.jpg";
import Avatar1 from "../assets/avatars/Avatar1.jpg";
import Avatar2 from "../assets/avatars/Avatar2.jpg";
import Avatar3 from "../assets/avatars/Avatar3.jpg";
import Avatar4 from "../assets/avatars/Avatar4.jpg";
import Avatar5 from "../assets/avatars/Avatar5.jpg";
import Avatar6 from "../assets/avatars/Avatar6.jpg";
import Avatar7 from "../assets/avatars/Avatar7.jpg";
import Avatar8 from "../assets/avatars/Avatar8.jpg";
import Avatar9 from "../assets/avatars/Avatar9.jpg";
import Avatar10 from "../assets/avatars/Avatar10.jpg";
import Avatar11 from "../assets/avatars/Avatar11.jpg";
import Avatar12 from "../assets/avatars/Avatar12.jpg";
import Avatar13 from "../assets/avatars/Avatar13.jpg";
import Avatar14 from "../assets/avatars/Avatar14.jpg";
import Avatar15 from "../assets/avatars/Avatar15.jpg";
import Avatar16 from "../assets/avatars/Avatar16.jpg";
import Avatar17 from "../assets/avatars/Avatar17.jpg";
import Avatar18 from "../assets/avatars/Avatar18.jpg";
import Avatar19 from "../assets/avatars/Avatar19.jpg";
import Avatar20 from "../assets/avatars/Avatar20.jpg";
import Avatar21 from "../assets/avatars/Avatar21.jpg";
import Avatar22 from "../assets/avatars/Avatar22.jpg";
import Avatar23 from "../assets/avatars/Avatar23.jpg";

const avatarImages = {
  Avatar0,
  Avatar1,
  Avatar2,
  Avatar3,
  Avatar4,
  Avatar5,
  Avatar6,
  Avatar7,
  Avatar8,
  Avatar9,
  Avatar10,
  Avatar11,
  Avatar12,
  Avatar13,
  Avatar14,
  Avatar15,
  Avatar16,
  Avatar17,
  Avatar18,
  Avatar19,
  Avatar20,
  Avatar21,
  Avatar22,
  Avatar23,
};

const Header = ({ accessiblePages = [] }) => {
  const { firstName, lastName, role, avatar } = useContext(UserContext);
  const navigate = useNavigate();
  const location = useLocation();
  const [shouldFadeIn, setShouldFadeIn] = useState(false);
  const { handleProtectedNavigation } = useUnsavedChanges();

  // Training system
  const navigation = useTrainingSection("general", "nav_header");

  useEffect(() => {
    setShouldFadeIn(true);
  }, []);

  const isAuthPage =
    location.pathname === "/login" || location.pathname === "/signup";
  const isLoggedIn =
    !!firstName || !!lastName || (role && role !== "DefaultRole");

  const handleLogoClick = () => {
    handleProtectedNavigation(() => navigate("/home"));
  };

  const handleAvatarClick = () => {
    handleProtectedNavigation(() => navigate("/profile"));
  };

  // Function to determine if a nav item is active
  const isActivePage = (path) => {
    return (
      location.pathname === path || location.pathname.startsWith(path + "/")
    );
  };

  // Function to handle navigation
  const handleNavigation = (path) => {
    handleProtectedNavigation(() => navigate(path));
  };

  return (
    <header className={`app-header ${shouldFadeIn ? "fade-in" : ""}`}>
      <div
        className="app-header-left"
        onClick={handleLogoClick}
        style={{ cursor: "pointer" }}
      >
        <img src={logo} className="app-logo" alt="logo" />
        <h1 className="app-name">SocratesLab</h1>
      </div>

      {!isAuthPage && isLoggedIn && (
        <div
          ref={navigation.ref}
          className={`app-header-nav ${navigation.className}`}
          onMouseEnter={navigation.onMouseEnter}
        >
          <div
            className={`nav-item ${isActivePage("/home") ? "active" : ""}`}
            onClick={() => handleNavigation("/home")}
          >
            <FontAwesomeIcon icon={faHome} className="nav-icon" />
            <span>Home</span>
          </div>

          {accessiblePages.includes("my-courses") && (
            <div
              className={`nav-item ${
                isActivePage("/my-courses") ? "active" : ""
              }`}
              onClick={() => handleNavigation("/my-courses")}
            >
              <FontAwesomeIcon icon={faBookOpen} className="nav-icon" />
              <span>My Courses</span>
            </div>
          )}

          {accessiblePages.includes("courses") && (
            <div
              className={`nav-item ${isActivePage("/courses") ? "active" : ""}`}
              onClick={() => handleNavigation("/courses")}
            >
              <FontAwesomeIcon icon={faBookOpen} className="nav-icon" />
              <span>Courses</span>
            </div>
          )}

          {accessiblePages.includes("class-lists") && (
            <div
              className={`nav-item ${
                isActivePage("/class-lists") ? "active" : ""
              }`}
              onClick={() => handleNavigation("/class-lists")}
            >
              <FontAwesomeIcon icon={faFileAlt} className="nav-icon" />
              <span>Class Lists</span>
            </div>
          )}

          {accessiblePages.includes("user-management") && (
            <div
              className={`nav-item ${
                isActivePage("/user-management") ? "active" : ""
              }`}
              onClick={() => handleNavigation("/user-management")}
            >
              <FontAwesomeIcon icon={faUser} className="nav-icon" />
              <span>User Management</span>
            </div>
          )}

          {accessiblePages.includes("gradebook") && (
            <div
              className={`nav-item ${
                isActivePage("/gradebook") ? "active" : ""
              }`}
              onClick={() => handleNavigation("/gradebook")}
            >
              <FontAwesomeIcon icon={faGraduationCap} className="nav-icon" />
              <span>Gradebook</span>
            </div>
          )}

          {accessiblePages.includes("incident-management") && (
            <div
              className={`nav-item ${
                isActivePage("/incident-management") ? "active" : ""
              }`}
              onClick={() => handleNavigation("/incident-management")}
            >
              <FontAwesomeIcon icon={faShieldAlt} className="nav-icon" />
              <span>Incidents</span>
            </div>
          )}

          {accessiblePages.includes("ai-tutors") && (
            <div
              className={`nav-item ${
                isActivePage("/ai-tutors") ? "active" : ""
              }`}
              onClick={() => handleNavigation("/ai-tutors")}
            >
              <FontAwesomeIcon icon={faRobot} className="nav-icon" />
              <span>AI Tutors</span>
            </div>
          )}

          {accessiblePages.includes("my-tutor-conversations") && (
            <div
              className={`nav-item ${
                isActivePage("/my-tutor-conversations") ? "active" : ""
              }`}
              onClick={() => handleNavigation("/my-tutor-conversations")}
            >
              <FontAwesomeIcon icon={faRobot} className="nav-icon" />
              <span>AI Tutors</span>
            </div>
          )}
        </div>
      )}

      {!isAuthPage && isLoggedIn && (
        <div className="app-header-right">
          <img
            src={avatarImages[avatar || "Avatar0"]}
            alt="User Avatar"
            className="header-avatar"
            onClick={handleAvatarClick}
            style={{ cursor: "pointer" }}
          />
          <div className="header-user-info">
            <p className="app-header-text">
              {firstName} {lastName}
            </p>
            <p className="app-header-role">Your role: {role}</p>
            <LogoutButton />
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
